<template>
  <VDialogV3
    id="product-card"
    position="right"
    :width="dialogWidth"
    height="100vh"

    :tabs="tabs"
    v-model:active-tab="activeTab"

    :context-menu="ProductsService.contextMenu"
    :entity="localProduct"
    :submit-method="submit"
  >
    <!-- v-show используется специально, чтобы мы смогли провалидировать каждую вкладку.
 если писать v-if контролы не смонтируются и мы не сможем их провалидировать -->
    <fieldset v-show="activeTab.id === 'main'" data-tab-id="main">
      <section>
        <VSelectV2
          :model-value="localProduct.category"
          label="Категория"
          required
          :data="({ pattern }) => ProductCategoriesService.getList({ pattern })"
          :validate-method="validateCategory"
          layout="vertical"
          hide-clear-btn
          @update:modelValue="localProduct.setCategory($event)"
        />

        <VInput
          v-model="localProduct.name"
          required
          label="Название"
          layout="vertical"
          :max-length="300"
        >
          <template #helper-text>
            По названию вы сможете проще отличать разные товары
          </template>
        </VInput>

        <VFormItem>
          <VInput
            ref="buyersCode"
            v-model="localProduct.code"
            label="Артикул"
            layout="vertical"
            :max-length="30"
            :validate-method="onCodeValidate"
          />

          <VSelectV2
            v-model="localProduct.unit"
            required
            hide-clear-btn
            label="Ед измерения"
            layout="vertical"
            :data="unitLoader"
          />
          <template #helper-text>
            Код товара в вашей системе учета
          </template>
        </VFormItem>

        <VSelectV2
          v-model="localProduct.actualStatus"
          required
          hide-clear-btn
          label="Статус актуальности"
          layout="vertical"
          :data="ProductsService.getActualStatusList() || []"
        />

        <VTextarea
          v-model="localProduct.description"
          label="Описание"
          layout="vertical"
          :max-length="500"
          :min-height="65"
          auto-height
        />

        <VTextarea
          v-model="localProduct.descriptionForSeller"
          label="Описание для поставщика"
          layout="vertical"
          :max-length="500"
          :min-height="65"
          auto-height
        />

        <VSelectV2
          v-model="localProduct.expirationDays"
          required
          label="Остаточный срок годности"
          layout="vertical"
          :data="ProductsService.expirationDaysRemainingList"
        />

        <VInputNumberRusV2
          v-model="localProduct.productionRequiredPackageVolume"
          label="Фасовка (требование производства)"
          layout="vertical"
          :type="Qty"
          :unit="localProduct.unit?.name"
        />
      </section>

      <hr>
      <BudgetPricesWidget :product="localProduct"/>

      <hr>
      <section>
        <VSelectV2
          v-model="localProduct.manufacturers"
          label="Производитель"
          layout="vertical"
          :data="({ pattern }) => ManufacturersService.getList({ pattern })"
          :disabled="!isAssigned"
          multiple
        />
      </section>

      <hr>
      <section>
        <VSelectV2
          v-model="localProduct.responsible"
          label="Ответственный"
          placeholder="Введите ФИО ответственного"
          layout="vertical"
          :data="({ pattern }) => PersonsService.searchPersons(pattern, $core.currentUser.partner.id)"
        />
      </section>

      <hr>
      <section>
        <VSwitch
          v-model="localProduct.isService"
          label="Услуга"
          layout="vertical"
          theme="slider"
          disabled
        />
      </section>
    </fieldset>
    <!-- v-show используется специально, чтобы мы смогли провалидировать каждую вкладку.
     если писать v-if контролы не смонтируются и мы не сможем их провалидировать -->
    <fieldset v-show="activeTab.id === 'characteristic'" data-tab-id="characteristic">
      <ProductCharacteristics :product="localProduct"/>
    </fieldset>

    <SupplyOffersRegistry
      v-if="activeTab.id === 'supplyOffers'"
      data-tab-id="supplyOffers"
      v-model:product="localProduct"
    />

    <fieldset v-if="activeTab.id === 'files'" data-tab-id="files">
      <FilesList :entities="filesEntities"/>
    </fieldset>
  </VDialogV3>
</template>

<script lang="ts">

import Product from '@/domains/Products/Product';
import ProductsService from '@/domains/Products/ProductsService';
import Qty from '@/core/types/Qty';
import ProductCategory from '@/domains/ProductCategories/ProductCategory';
import ProductCategoriesService from '@/domains/ProductCategories/ProductCategoriesService';
import ManufacturersService from '@/domains/Manufacturers/ManufacturersService';
import EntityPointer from '@/domains/EntityPointer/EntityPointer';
import PersonsService from '@/domains/Persons/PersonsService';
import SelectLoader from "@/lib/components/VSelectV2/SelectLoader";

import SupplyOffersRegistry
  from '@/domains/SupplyOffers/components/SupplyOffersRegistry/SupplyOffersRegistry.vue';
import ProductCharacteristics
  from '@/domains/Products/components/ProductCharacteristics/ProductCharacteristics.vue';
import BudgetPricesWidget from "@/domains/BudgetPrices/components/BudgetPricesWidget/BudgetPricesWidget.vue";

export default defineComponent({
  name: 'ProductCard',

  components: {
    SupplyOffersRegistry,
    ProductCharacteristics,
    BudgetPricesWidget,
  },

  props: {
    product: {
      type: Object,
      default: null,
    },
    id: {
      type: Number,
      default: null,
    },
  },

  setup() {
    return {
      PersonsService,
      ProductsService,
      ProductCategoriesService,
      ManufacturersService,
      Qty,
    }
  },

  data() {

    const isExistingProduct = this.$core.utils.isNullOrUndefined(this.product?.id ?? this.id) === false;
    /** ------- unitLoader ------- **/

    /**
     * Единицы измерения берутся из категории — она их и несёт, запроса нет.
     * Зависимость нужна, чтобы при смене категории список обновился, а
     * единица не из нового списка сбросилась.
     */
    const unitLoader = new SelectLoader<Product>(async ({ productCategory }) =>
      (await ProductCategoriesService.get(productCategory?.id, { requireAllowedUnits: true }))?.allowedUnits
      ?? []
    )
      .dependsOn({
        productCategory: () => this.localProduct?.category ?? null,
      })
      .preloadOnMount(isExistingProduct === false)
      .preloadOnFocus(isExistingProduct);

    return {
      localProduct: undefined as unknown as Product,
      unitLoader,

      /** Категория товара на момент открытия карточки */
      initialCategoryId: null as number | null,

      activeTab: {id: 'main', name: 'Основное'},
    };
  },

  computed: {
    /** На вкладке разрешений таблица широкая, остальным вкладкам лишнее место ни к чему */
    dialogWidth(): string {
      if (this.activeTab.id === 'supplyOffers') {
        return '1210px';
      }
      else {
        return '605px';
      }
    },

    isAssigned(): boolean {
      return Boolean(this.localProduct?.category?.id);
    },

    tabs() {
      const tabs = [
        {id: 'main', name: 'Основное'},
        {id: 'characteristic', name: 'Характеристики'},
      ];

      if (this.localProduct?.isExisting) {
        tabs.push({id: 'supplyOffers', name: 'Поставщики'});
        tabs.push({id: 'files', name: 'Файлы'});
      }

      return tabs;
    },

    filesEntities() {
      if (this.localProduct === undefined) return [];

      return [
        new EntityPointer('product', this.localProduct.id, this.localProduct.name)
      ].concat(
        this.localProduct.supplyOffers.map(
          (supplyOffer) => new EntityPointer('supplyOffer', supplyOffer.id, `Разрешение на поставку для поставщика ${ supplyOffer.sellerCompany?.name } с производителем ${supplyOffer.manufacturer?.name}`)
        )
      )
    },
  },

  async mounted() {
    await this.init();

    /** Этот сумасшедший хак нужен, чтобы селектор производителя отображал в дропдауне
     * список чекнутых позиций, в противном случае он не отображает */
    this.localProduct!.manufacturers = [...this.localProduct!.manufacturers];

  },

  methods: {
    async init(): Promise<void> {
      if (this.id) {
        this.localProduct = await ProductsService.get(this.id);
      } else if (this.product instanceof Product) {
        this.localProduct = this.$core.utils.cloneDeep(this.product);
      } else {
        this.localProduct = Product.getEmpty();
      }

      this.initialCategoryId = this.localProduct.category?.id ?? null;
    },

    async submit(): Promise<boolean> {
      let success: boolean;

      if (this.localProduct.isExisting === true) {
        success = await ProductsService.update(this.localProduct);
      }
      else {
        success = await ProductsService.add(this.localProduct);
      }

      return success;
    },

    /** Проверяем только выбранную заново категорию — как и бэк: товары, уже висящие на ветках, остаются редактируемыми */
    validateCategory(category: ProductCategory | null): string | null {
      if (this.$core.utils.isNullOrUndefined(category) === true) {
        return null;
      }
      else if (category.id === this.initialCategoryId) {
        return null;
      }
      else {
        return ProductCategoriesService.getReasonCannotAttachProduct(category);
      }
    },

    async onCodeValidate(value: string): Promise<string | null> {
      if (!value || this.localProduct?.code === value) {
        return null;
      }

      const products = await ProductsService.getList({code: value});
      return products.length === 0 ? null : 'Артикул уже занят';
    },
  },
});
</script>